import type { CSSProperties, ReactNode } from "react";

import Refresh from "@mui/icons-material/Refresh";
import { Box, Button, Skeleton, Typography } from "@mui/material";

import styles from "./AuditLogList.module.scss";

export type AuditLogIconVariant =
  | "timeline"
  | "status"
  | "update"
  | "share"
  | "system"
  | "success"
  | "error"
  | "warning"
  | "secondary"
  | "info";

/** Custom color scheme for the icon circle. Use when none of the preset variants fit. */
export interface AuditLogIconStyle {
  /** Icon glyph color. Also used as border color when `borderColor` is omitted. */
  color: string;
  /** Optional override for the circle's border color. */
  borderColor?: string;
}

export interface AuditLogEntry {
  id: string;
  /** ISO timestamp. Used for day-header grouping. */
  timestamp: string;
  /** Main action text. */
  primaryText: string;
  /** Optional node rendered after the primary text (e.g. inline tag or tooltip). */
  primaryAdornment?: ReactNode;
  /** Sub-line beneath the action (typically actor + time). */
  secondaryText: ReactNode;
  /** Uppercase tag rendered on the right. */
  label: string;
  /** Icon node rendered inside the colored circle. Any ReactNode (MUI icon, SVG, emoji, etc.). */
  icon: ReactNode;
  /** Color scheme of the icon circle. Either a preset variant or a custom color object. */
  iconVariant: AuditLogIconVariant | AuditLogIconStyle;
}

export interface AuditLogListProps {
  /**
   * Entries to render. Sorted internally by `timestamp` (most recent first)
   * before rendering, so day headers stay correct regardless of input order.
   */
  entries: AuditLogEntry[];
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  /** Optional title above the list. */
  title?: string;
  /** Message shown when entries is empty and not loading. */
  emptyMessage?: string;
  /** Number of skeleton rows shown while loading. */
  skeletonCount?: number;
  /** Whether to insert per-day headers (Today / Yesterday / Date). Default true. */
  showDayHeaders?: boolean;
}

const ICON_VARIANT_CLASS: Record<AuditLogIconVariant, string> = {
  timeline: styles.iconTimeline,
  status: styles.iconStatus,
  update: styles.iconUpdate,
  share: styles.iconShare,
  system: styles.iconSystem,
  success: styles.iconSuccess,
  error: styles.iconError,
  warning: styles.iconWarning,
  secondary: styles.iconSecondary,
  info: styles.iconInfo,
};

function IconCircle({
  variant,
  children,
}: {
  variant: AuditLogIconVariant | AuditLogIconStyle;
  children: ReactNode;
}) {
  if (typeof variant === "string") {
    return (
      <Box className={`${styles.icon} ${ICON_VARIANT_CLASS[variant]}`}>
        {children}
      </Box>
    );
  }
  const style: CSSProperties = {
    color: variant.color,
    borderColor: variant.borderColor ?? variant.color,
  };
  return (
    <Box className={styles.icon} style={style}>
      {children}
    </Box>
  );
}

function dayKey(isoString: string): string {
  return new Date(isoString).toDateString();
}

function formatDayHeader(isoString: string): string {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const key = dayKey(isoString);
  if (key === today.toDateString()) return "Today";
  if (key === yesterday.toDateString()) return "Yesterday";

  return new Date(isoString).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function AuditLogList({
  entries,
  loading = false,
  error = null,
  onRetry,
  title,
  emptyMessage = "No activity recorded yet.",
  skeletonCount = 4,
  showDayHeaders = true,
}: AuditLogListProps) {
  if (loading) {
    return (
      <Box className={styles.list}>
        {Array.from({ length: skeletonCount }).map((_, i) => (
          <Box key={i} className={styles.item}>
            <Skeleton
              variant="circular"
              width={16}
              height={16}
              sx={{ mt: 0.5 }}
            />
            <Box sx={{ flex: 1 }}>
              <Skeleton
                variant="text"
                width="70%"
                sx={{ fontSize: "0.95rem" }}
              />
              <Skeleton
                variant="text"
                width="48%"
                sx={{ fontSize: "0.825rem" }}
              />
            </Box>
            <Skeleton variant="text" width={58} sx={{ fontSize: "0.7rem" }} />
          </Box>
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={styles.errorContainer}>
        <Typography variant="body2" color="error">
          {error}
        </Typography>
        {onRetry && (
          <Button
            size="small"
            startIcon={<Refresh fontSize="small" />}
            onClick={onRetry}
          >
            Retry
          </Button>
        )}
      </Box>
    );
  }

  if (entries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyMessage}
      </Typography>
    );
  }

  const sortedEntries = [...entries].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  );

  return (
    <Box className={styles.container}>
      {title && (
        <Typography variant="body2" className={styles.title}>
          {title}
        </Typography>
      )}
      <Box className={styles.list}>
        {sortedEntries.map((entry, index) => {
          const showDayHeader =
            showDayHeaders &&
            (index === 0 ||
              dayKey(sortedEntries[index - 1].timestamp) !==
                dayKey(entry.timestamp));
          const isLast = index === sortedEntries.length - 1;

          return (
            <Box key={entry.id}>
              {showDayHeader && (
                <Typography className={styles.dayHeader}>
                  {formatDayHeader(entry.timestamp)}
                </Typography>
              )}
              <Box
                className={`${styles.item} ${!isLast ? styles.itemWithDivider : ""}`}
              >
                <IconCircle variant={entry.iconVariant}>
                  {entry.icon}
                </IconCircle>
                <Box className={styles.content}>
                  <Box className={styles.primaryRow}>
                    <Typography className={styles.primaryText}>
                      {entry.primaryText}
                    </Typography>
                    {entry.primaryAdornment}
                  </Box>
                  <Typography component="div" className={styles.secondaryText}>
                    {entry.secondaryText}
                  </Typography>
                </Box>
                <Typography className={styles.label}>{entry.label}</Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
